/**
 * VCloud config-table helpers — ported 1-1 from legacy AuditCloud transformer.
 * Parses VM specs from the configoption name, classifies resource types, and
 * folds the flat configoptions (+ addon services) into service → type → item
 * groups with rolled-up vCores/RAM/storage/IP/bandwidth totals.
 */
import type { ServiceConfigOption, ServiceRow, ServiceDetail } from '@/api/services';

export type VmServiceType = 'vm_instance' | 'vm_lb' | 'other_service';

export interface ResourceTotals {
  vCores: number;
  ram: number;
  storage: number;
  publicIP: number;
  floatingIP: number;
  bandwidth: number;
  price: number;
}

export interface VCloudGroup {
  serviceId: number;
  serviceName: string;
  serviceStatus?: string;
  isMain: boolean;
  pricingModel?: string;
  priceRegister?: number;
  employeeNames?: string[];
  startDate?: string | null;
  stoppedDate?: string | null;
  currentStart?: string | null;
  currentEnd?: string | null;
  continueStart?: string | null;
  continueEnd?: string | null;
  totals: ResourceTotals;
  types: { key: VmServiceType; label: string; items: ServiceConfigOption[] }[];
}

/** "v2g-standard-8-32" → 8 vCores, 32 GB RAM (trailing "-cores-ram"). */
export function parseVMConfig(name?: string): { cores: number; ram: number } {
  const m = (name || '').match(/(\d+)-(\d+)$/);
  return m ? { cores: parseInt(m[1], 10), ram: parseInt(m[2], 10) } : { cores: 0, ram: 0 };
}

const has = (t = '', n = '', ...needles: string[]) => needles.some((k) => t.includes(k) || n.includes(k));
export const isStorage = (type?: string, name?: string) => has(type?.toLowerCase(), name?.toLowerCase(), 'storage', 'disk', 'ssd', 'hdd');
export const isFloatingIP = (type?: string, name?: string) => has(type?.toLowerCase(), name?.toLowerCase(), 'floating');
export function isPublicIP(type?: string, name?: string) {
  const t = type?.toLowerCase() || '', n = name?.toLowerCase() || '';
  if (t.includes('floating') || n.includes('floating')) return false;
  return (t.includes('public') && t.includes('ip')) || (n.includes('public') && n.includes('ip'));
}
export const isBandwidth = (type?: string, name?: string) => has(type?.toLowerCase(), name?.toLowerCase(), 'bandwidth', 'bw');

export function serviceTypeOf(item: ServiceConfigOption): VmServiceType {
  const type = item.type?.toLowerCase() || '', name = item.name?.toLowerCase() || '';
  if (type === 'vm-instance' || name.includes('instance')) return 'vm_instance';
  if (type === 'vm-lb' || name.includes('lb') || name.includes('load') || name.includes('balancer')) return 'vm_lb';
  return 'other_service';
}

const emptyTotals = (): ResourceTotals => ({ vCores: 0, ram: 0, storage: 0, publicIP: 0, floatingIP: 0, bandwidth: 0, price: 0 });

function accumulate(items: ServiceConfigOption[]): ResourceTotals {
  return items.reduce((tot, item) => {
    const qty = item.commitment_value || 0;
    const kind = serviceTypeOf(item);
    if (kind === 'vm_instance' || kind === 'vm_lb') {
      const cfg = parseVMConfig(item.name);
      tot.vCores += cfg.cores * qty;
      tot.ram += cfg.ram * qty;
    } else {
      if (isStorage(item.type, item.name)) tot.storage += qty;
      if (isPublicIP(item.type, item.name)) tot.publicIP += qty;
      if (isFloatingIP(item.type, item.name)) tot.floatingIP += qty;
      if (isBandwidth(item.type, item.name)) tot.bandwidth += qty;
    }
    tot.price += item.commitment_price_total || (item.commitment_price_per_unit || 0) * qty;
    return tot;
  }, emptyTotals());
}

const withCommitment = (list?: ServiceConfigOption[]) => (list ?? []).filter((c) => (c.commitment_value ?? 0) > 0);

const TYPE_ORDER: VmServiceType[] = ['vm_instance', 'vm_lb', 'other_service'];
const TYPE_LABEL: Record<VmServiceType, string> = { vm_instance: 'VM Instance', vm_lb: 'Load Balancer', other_service: 'Other resources' };

function toGroup(id: number, name: string, status: string | undefined, isMain: boolean, items: ServiceConfigOption[], meta: Partial<VCloudGroup>): VCloudGroup {
  const types = TYPE_ORDER.map((key) => ({ key, label: TYPE_LABEL[key], items: items.filter((i) => serviceTypeOf(i) === key) })).filter((g) => g.items.length);
  return { serviceId: id, serviceName: name, serviceStatus: status, isMain, totals: accumulate(items), types, ...meta };
}

/** Build the ordered group model + grand totals for the hierarchical VCloud table. */
export function buildVCloudGroups(
  detail: ServiceDetail,
  base: ServiceConfigOption[],
  addons: (ServiceRow & { configoptions?: ServiceConfigOption[] })[],
): { groups: VCloudGroup[]; grand: ResourceTotals & { priceRegister: number; combined: number } } {
  const groups: VCloudGroup[] = [];
  const mainItems = withCommitment(base);
  if (mainItems.length) {
    groups.push(toGroup(detail.service_id, detail.service_name, detail.service_status, true, mainItems, {
      pricingModel: detail.service_pricing_model,
      priceRegister: detail.service_price_register,
      employeeNames: detail.employee_names,
      startDate: detail.service_start_date,
      stoppedDate: detail.service_stopped_date,
      currentStart: detail.current_startdate,
      currentEnd: detail.current_enddate,
      continueStart: detail.continue_startdate,
      continueEnd: detail.continue_enddate,
    }));
  }
  for (const a of addons) {
    const items = withCommitment(a.configoptions);
    if (!items.length) continue;
    groups.push(toGroup(a.service_id, a.service_name, a.service_status, false, items, {
      pricingModel: a.service_pricing_model,
      priceRegister: a.service_price_register,
      employeeNames: detail.employee_names,
      startDate: a.service_start_date ?? detail.service_start_date,
      currentStart: a.current_startdate ?? detail.current_startdate,
      currentEnd: a.current_enddate ?? detail.current_enddate,
      continueStart: a.continue_startdate ?? detail.continue_startdate,
      continueEnd: a.continue_enddate ?? detail.continue_enddate,
    }));
  }

  const excluded = new Set(['cancelled', 'rejected', 'pending-terminated']);
  const mainExcluded = excluded.has(detail.service_status);
  const grand = { ...emptyTotals(), priceRegister: 0, combined: 0 };
  for (const g of groups) {
    if (!mainExcluded && excluded.has(g.serviceStatus ?? '')) continue;
    grand.vCores += g.totals.vCores; grand.ram += g.totals.ram; grand.storage += g.totals.storage;
    grand.publicIP += g.totals.publicIP; grand.floatingIP += g.totals.floatingIP; grand.bandwidth += g.totals.bandwidth;
    grand.price += g.totals.price;
    grand.priceRegister += g.priceRegister || 0;
    grand.combined += g.pricingModel === 'package' ? g.priceRegister || 0 : g.totals.price;
  }
  return { groups, grand };
}
